/**
 * Resolves the stored payment method that matches the tokenized payment id.
 * Prefers an explicit storedPaymentMethods list (e.g. from getPaymentMethods).
 */
function findStoredPaymentMethod(walletResult, storedPaymentMethodId) {
  const storedMethods = walletResult?.storedPaymentMethods;
  if (!Array.isArray(storedMethods) || !storedPaymentMethodId) {
    return null;
  }
  return (
    storedMethods.find((method) => method?.id === storedPaymentMethodId) || null
  );
}

/**
 * Maps GMWallet / getPaymentMethods fields onto AuthorizationApiRequest:
 * - payment.id | id → storedPaymentMethodId
 * - brand → cardBrand
 * - lastFour → cardLast4Digits
 */
export function buildPaymentFields(walletResult) {
  if (!walletResult) {
    return {};
  }

  const storedPaymentMethodId =
    walletResult.payment?.id ||
    walletResult.storedPaymentMethodId ||
    walletResult.id;

  if (!storedPaymentMethodId) {
    return {};
  }

  const stored = findStoredPaymentMethod(walletResult, storedPaymentMethodId);
  const cardBrand =
    walletResult.cardBrand ||
    walletResult.brand ||
    walletResult.payment?.brand ||
    stored?.brand;
  const cardLast4Digits =
    walletResult.cardLast4Digits ||
    walletResult.lastFour ||
    walletResult.payment?.lastFour ||
    stored?.lastFour;

  return {
    storedPaymentMethodId,
    ...(cardBrand ? { cardBrand } : {}),
    ...(cardLast4Digits ? { cardLast4Digits } : {}),
  };
}

export function buildOrderEmailFields(formValues) {
  return {
    additionalEmails: (formValues.additionalEmails || []).filter(Boolean),
  };
}

export function buildPlaceOrderRequest({
  walletResult,
  formValues,
  extraFields = {},
}) {
  return {
    ...buildPaymentFields(walletResult),
    ...buildOrderEmailFields(formValues),
    ...extraFields,
  };
}

/**
 * Builds KeycodePurchaseRequest: auth fields nest under authorizationRequest.
 */
export function buildKeycodePurchaseRequest({ orderRequest, formValues }) {
  const {
    storedPaymentMethodId,
    cardBrand,
    cardLast4Digits,
    additionalEmails,
    passcode,
    cId,
    cText,
  } = orderRequest;

  return {
    authorizationRequest: {
      ...(storedPaymentMethodId ? { storedPaymentMethodId } : {}),
      ...(cardBrand ? { cardBrand } : {}),
      ...(cardLast4Digits ? { cardLast4Digits } : {}),
      ...(additionalEmails?.length ? { additionalEmails } : {}),
    },
    vin: formValues.vin,
    vehicleOwnerName: formValues.vehicleOwnerName,
    licensePlate: formValues.licensePlate,
    passcode,
    cId,
    cText,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
}
